import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import {
    FormHelp,
    Label,
    LabeledControl,
    Radio,
    Spacer,
    Text,
    Flex,
    List,
} from '@zillow/constellation';
import { Tag } from '@zillow/zrs-comet';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import appLogger from 'app/shared/loggers/appLogger';

import { StyledListItem } from './StyledComponents';

const propTypes = {
    errorObj: PropTypes.object.isRequired,
    handleSelectionChange: PropTypes.func.isRequired,
    isApplicationsAccepted: PropTypes.bool,
    isApplicationsAcceptedRef: PropTypes.object.isRequired,
};

const defaultProps = {
    isApplicationsAccepted: null,
};

const RADIO_OPTIONS = {
    YES: { VALUE: true },
    NO: { VALUE: false },
};

const EnableApplicationsRadio = ({
    errorObj: { isApplicationsAccepted: errorMessage },
    handleSelectionChange,
    isApplicationsAccepted,
    isApplicationsAcceptedRef,
}) => {
    const dispatch = useDispatch();
    const [selectedOption, setSelectedOption] = useState(isApplicationsAccepted);

    const getEventAction = (selected) => {
        let fromSelectedDisplay = selectedOption;
        const toSelectedDisplay = selected ? 'Enable' : 'Disable';

        if (fromSelectedDisplay !== null) {
            fromSelectedDisplay = fromSelectedDisplay ? 'Enabled' : 'Disabled';
        }

        return `${toSelectedDisplay}From${fromSelectedDisplay}`;
    };

    const triggerGAEvent = (selected) => {
        const eventAction = getEventAction(selected);
        const { APPLICATIONS_RADIO_CHANGE } = AnalyticsConstants.GA_EVENTS;

        APPLICATIONS_RADIO_CHANGE.action = eventAction;

        appLogger.logInfo({
            message: `INFO: ${JSON.stringify(APPLICATIONS_RADIO_CHANGE)}`,
            context: 'EnableApplicationsRadio.triggerGAEvent',
        });

        dispatch(analyticsUtils.track.GAEventV2(APPLICATIONS_RADIO_CHANGE));
    };

    const handleChange = (selected) => {
        setSelectedOption(selected);
        triggerGAEvent(selected);
        handleSelectionChange('isApplicationsAccepted', selected);
    };

    return (
        <div>
            <Spacer marginBottom="xl">
                <div ref={isApplicationsAcceptedRef} />
                <Text marginBottom="xs" fontType="h4" as="div">Receive Zillow applications?</Text>
                <Text marginBottom="xs" as="div">
                    Access all applications &#38; documents in one place -- for free!
                </Text>
                <Text as="div">
                    You&#39;ll automatically receive:
                </Text>
                <List>
                    <StyledListItem><Text>Credit report</Text></StyledListItem>
                    <StyledListItem><Text>Background check</Text></StyledListItem>
                    <StyledListItem>
                        <Text>Income &#38; employment history</Text>
                    </StyledListItem>
                    <StyledListItem>
                        <Flex display="flex">
                            <Text>Proof-of-income (if provided)</Text>
                            <Spacer marginLeft="xs">
                                <Tag appearance="success" size="sm">NEW</Tag>
                            </Spacer>
                        </Flex>
                    </StyledListItem>
                </List>
                <Spacer marginY="xs">
                    <LabeledControl
                        controlId="LabeledControl-EnableApplicationsRadio1"
                        label={(
                            <Label>
                                <Text>
                                    Yes, receive Zillow applications
                                </Text>
                            </Label>
                        )}
                        control={(
                            <Radio
                                name="EnableApplicationsRadio"
                                checked={selectedOption === RADIO_OPTIONS.YES.VALUE}
                                onChange={() => handleChange(RADIO_OPTIONS.YES.VALUE)}
                            />
                        )}
                    />
                    <LabeledControl
                        controlId="LabeledControl-EnableApplicationsRadio2"
                        label={(
                            <Label>
                                <Text>
                                    No, don&#39;t receive Zillow applications
                                </Text>
                            </Label>
                        )}
                        control={(
                            <Radio
                                name="EnableApplicationsRadio"
                                checked={selectedOption === RADIO_OPTIONS.NO.VALUE}
                                onChange={() => handleChange(RADIO_OPTIONS.NO.VALUE)}
                            />
                        )}
                    />
                </Spacer>
                {errorMessage && <FormHelp error={true}>{errorMessage}</FormHelp>}
            </Spacer>
        </div>
    );
};

EnableApplicationsRadio.propTypes = propTypes;
EnableApplicationsRadio.defaultProps = defaultProps;

export { EnableApplicationsRadio };
export default EnableApplicationsRadio;
